import {
  createTemplateSchema,
  hasPermission,
  MESSAGE_CHANNEL_LABELS,
  MESSAGE_CHANNELS,
  PLACEHOLDER_DESCRIPTIONS,
  TEMPLATE_PLACEHOLDERS,
  type CreateTemplateInput,
} from '@crm/shared'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { FileText, Lock, Plus } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Badge, Card, Field, Skeleton } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select, Textarea } from '@/components/ui/select'
import {
  templatesQuery,
  useCreateTemplate,
  useDeleteTemplate,
  useUpdateTemplate,
  type Template,
} from '@/features/messaging/api'
import { ApiClientError } from '@/lib/api'
import { fetchMe } from '@/lib/auth-query'
import { Route as AppRoute } from '../_app'

export const Route = createFileRoute('/_app/templates')({
  beforeLoad: async ({ context }) => {
    const me = await fetchMe(context.queryClient)
    if (me && !hasPermission(me.user.role, 'templates:view')) {
      throw redirect({ to: '/' })
    }
  },
  component: TemplatesPage,
})

function TemplatesPage() {
  const { me } = AppRoute.useRouteContext()
  const canManage = me.permissions.includes('templates:manage')
  const { data, isLoading } = useQuery(templatesQuery)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Template | null>(null)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Message templates</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Reusable message bodies with placeholders like{' '}
            <code className="rounded bg-[var(--color-muted)] px-1">{'{{first_name}}'}</code>.
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />
            New template
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-3/4" />
        </div>
      )}

      {data && data.items.length === 0 && (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <FileText className="h-8 w-8 text-[var(--color-muted-foreground)]" />
          <p className="text-sm text-[var(--color-muted-foreground)]">No templates yet.</p>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Create a reusable template for your messages.
          </p>
        </Card>
      )}

      <div className="space-y-2">
        {data?.items.map((template) => (
          <Card key={template.id} className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-medium">{template.name}</h2>
                  <Badge variant="muted">{MESSAGE_CHANNEL_LABELS[template.channel]}</Badge>
                  {template.systemKind && (
                    <Badge variant="default">
                      <Lock className="mr-1 h-3 w-3" />
                      System
                    </Badge>
                  )}
                </div>
                {template.subject && (
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    Subject: {template.subject}
                  </p>
                )}
                <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--color-muted-foreground)]">
                  {template.body}
                </p>
              </div>
              {canManage && (
                <Button variant="outline" size="sm" onClick={() => setEditing(template)}>
                  Edit
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {creating && <TemplateDialog onClose={() => setCreating(false)} />}
      {editing && <TemplateDialog template={editing} onClose={() => setEditing(null)} />}
    </div>
  )
}

function TemplateDialog({ template, onClose }: { template?: Template; onClose: () => void }) {
  const create = useCreateTemplate()
  const update = useUpdateTemplate(template?.id ?? '')
  const del = useDeleteTemplate()
  const [error, setError] = useState<string | null>(null)
  const isEdit = Boolean(template)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CreateTemplateInput>({
    resolver: zodResolver(createTemplateSchema),
    defaultValues: {
      name: template?.name ?? '',
      channel: template?.channel ?? 'sms',
      subject: template?.subject ?? '',
      body: template?.body ?? '',
    },
  })

  const channel = watch('channel')

  function onSubmit(values: CreateTemplateInput) {
    setError(null)
    const onError = (e: unknown) =>
      setError(e instanceof ApiClientError ? e.message : 'Could not save the template')

    if (isEdit) {
      update.mutate(
        { name: values.name, subject: values.subject, body: values.body },
        { onSuccess: onClose, onError },
      )
    } else {
      create.mutate(values, { onSuccess: onClose, onError })
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto p-6">
        <h2 className="mb-4 text-lg font-semibold">{isEdit ? 'Edit template' : 'New template'}</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {error && (
            <div role="alert" className="text-sm text-[var(--color-destructive)]">
              {error}
            </div>
          )}

          <Field label="Name" htmlFor="name" error={errors.name?.message} required>
            <Input id="name" autoFocus {...register('name')} />
          </Field>

          <Field label="Channel" htmlFor="channel" error={errors.channel?.message} required>
            {/* Channel is fixed after creation — it decides subject/consent semantics. */}
            <Select id="channel" disabled={isEdit} {...register('channel')}>
              {MESSAGE_CHANNELS.map((ch) => (
                <option key={ch} value={ch}>
                  {MESSAGE_CHANNEL_LABELS[ch]}
                </option>
              ))}
            </Select>
          </Field>

          {channel === 'email' && (
            <Field label="Subject" htmlFor="subject" error={errors.subject?.message}>
              <Input id="subject" {...register('subject')} />
            </Field>
          )}

          <Field label="Message" htmlFor="body" error={errors.body?.message} required>
            <Textarea id="body" rows={5} {...register('body')} />
          </Field>

          <div className="rounded-md bg-[var(--color-muted)] p-3 text-xs">
            <p className="mb-1 font-medium">Placeholders</p>
            <ul className="space-y-0.5 text-[var(--color-muted-foreground)]">
              {TEMPLATE_PLACEHOLDERS.map((p) => (
                <li key={p}>
                  <code>{`{{${p}}}`}</code> — {PLACEHOLDER_DESCRIPTIONS[p]}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-between gap-2 pt-2">
            <div>
              {isEdit && !template!.systemKind && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-[var(--color-destructive)]"
                  loading={del.isPending}
                  onClick={() =>
                    del.mutate(template!.id, {
                      onSuccess: onClose,
                      onError: (e) =>
                        setError(e instanceof ApiClientError ? e.message : 'Could not delete'),
                    })
                  }
                >
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending || update.isPending}>
                {isEdit ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        </form>
      </Card>
    </div>
  )
}
